// Connessione WebRTC di una chiamata di prova GPT-Live. L'SDP viene scambiato
// dal nostro gateway (la chiave OpenAI resta sul server). Gli eventi JSON
// arrivano sul data channel 'oai-events'.
//
// Chiusura (voice-webrtc.md "End conversation", live-conversations.md "Usage and
// graceful close"): si manda session.close e si tengono vivi media e data
// channel finché non arriva session.closed; solo dopo si rilascia tutto. Se
// session.closed non arriva entro closeTimeoutMs si chiude comunque
// (finalizzazione non confermata).
export async function startLiveCall({ getAnswer, iceTimeoutMs = 10000, closeTimeoutMs = 3000 }) {
  const events = new EventTarget();
  const pc = new RTCPeerConnection();
  const audio = new Audio();
  audio.autoplay = true;

  let mic = null;
  let channel = null;
  let finished = false;
  let closeTimer = null;
  let stopping = null;
  let resolveFinished;
  const finishedPromise = new Promise((r) => { resolveFinished = r; });

  function teardown() {
    if (mic) mic.getTracks().forEach((t) => t.stop());
    if (channel) { try { channel.close(); } catch { /* già chiuso */ } }
    pc.close();
    audio.srcObject = null;
  }

  // Unico punto di fine chiamata: pulizia ed 'ended' una sola volta, qualunque
  // sia la causa (session.closed, canale chiuso, peer fallita, timeout).
  function finish() {
    if (finished) return;
    finished = true;
    clearTimeout(closeTimer);
    teardown();
    events.dispatchEvent(new Event('ended'));
    resolveFinished();
  }

  pc.addEventListener('track', (e) => { audio.srcObject = e.streams[0]; });
  pc.addEventListener('connectionstatechange', () => {
    // 'disconnected' è spesso un blip di rete transitorio che si riprende da
    // solo: trattarlo come fine chiamata chiuderebbe sessioni ancora vive.
    if (['failed', 'closed'].includes(pc.connectionState)) finish();
  });

  try {
    mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    mic.getTracks().forEach((t) => pc.addTrack(t, mic));
    channel = pc.createDataChannel('oai-events');
    channel.addEventListener('message', (e) => {
      let detail;
      try { detail = JSON.parse(e.data); } catch { return; /* ignora */ }
      events.dispatchEvent(new CustomEvent('event', { detail }));
      if (detail?.type === 'session.closed') finish();
    });
    channel.addEventListener('close', () => { finish(); });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await new Promise((resolve, reject) => {
      if (pc.iceGatheringState === 'complete') return resolve();
      const timer = setTimeout(() => reject(new Error('ice_timeout')), iceTimeoutMs);
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') { clearTimeout(timer); resolve(); }
      });
    });
    const answer = await getAnswer(pc.localDescription.sdp);
    await pc.setRemoteDescription({ type: 'answer', sdp: answer });
  } catch (err) {
    // Setup fallito a metà (mic negato, ICE in timeout, gateway che rifiuta
    // l'offer...): senza questa pulizia il microfono restava acceso e la
    // peer connection aperta anche se la chiamata non è mai partita.
    // La chiamata non è mai partita: niente 'ended' per chi non l'ha ricevuta.
    finished = true;
    teardown();
    throw err;
  }

  // Idempotente; la promise si risolve a pulizia fatta.
  function stop() {
    if (stopping) return stopping;
    stopping = finishedPromise;
    if (finished) return stopping;
    if (!channel || channel.readyState !== 'open') { finish(); return stopping; }
    closeTimer = setTimeout(finish, closeTimeoutMs);
    try {
      channel.send(JSON.stringify({ type: 'session.close' }));
    } catch {
      finish();
    }
    return stopping;
  }
  return { stop, events };
}
